import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Search, Mail, Phone, Building2, ShoppingCart } from 'lucide-react';
import { Input } from '../../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/customers`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.phone?.includes(searchTerm)
  );

  return (
    <div data-testid="customers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Customers</h1>
          <p className="font-body text-text-secondary">{items.length} registered customer{items.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={18} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, email or phone"
          className="pl-10 rounded-xl"
          data-testid="customer-search-input"
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border flex flex-col items-center justify-center h-48 text-text-muted">
          <Users size={48} className="mb-4 opacity-40" />
          <p className="font-body">{searchTerm ? 'No customers match your search' : 'No customers yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((item) => (
            <div key={item.id} className={`bg-white rounded-2xl border border-surface-border overflow-hidden flex flex-col hover:shadow-md transition-shadow ${!item.is_active ? 'opacity-60' : ''}`} data-testid={`customer-card-${item.id}`}>
              <div className="p-4 flex-1">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                    <span className="font-heading font-bold text-purple-600 text-sm">
                      {item.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-body font-semibold text-text-primary line-clamp-1">{item.name}</h3>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs text-text-muted font-body">
                    <Phone size={12} />
                    <span>{item.phone}</span>
                  </div>
                  {item.email && (
                    <div className="flex items-center gap-1.5 text-xs text-text-muted font-body">
                      <Mail size={12} />
                      <span className="truncate">{item.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-text-muted font-body">
                    <Building2 size={12} />
                    {item.apartment_name ? (
                      <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">{item.apartment_name}</span>
                    ) : (
                      <span className="text-text-muted">Not set</span>
                    )}
                  </div>
                </div>
                {item.created_at && (
                  <p className="font-body text-xs text-text-muted mt-3">Joined {new Date(item.created_at).toLocaleDateString()}</p>
                )}
              </div>
              {/* Orders this month */}
              <div className="border-t border-surface-border bg-brand-primary/5 px-4 py-2.5 flex items-center gap-2">
                <ShoppingCart size={14} className="text-brand-primary" />
                <span className="font-body text-sm font-semibold text-brand-primary">
                  {item.orders_this_month || 0} order{(item.orders_this_month || 0) !== 1 ? 's' : ''} this month
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Customers;
